import type { ViewType } from '@components/shelfDetails/utils'
import type { SEARCH_STATE } from '@routes/ProductFinder/Provider'

export const SKIP_REDIRECT_QUERY_PARAM_NAME = 'skipRedirect'

export const routesFactory = {
  root: '/',
  storeSelection: (skipRedirect?: boolean) => skipRedirect ? `/store?${SKIP_REDIRECT_QUERY_PARAM_NAME}=true` : '/store',
  home: (storeId: string, scanId?: string) => `${routesFactory.storeSelection()}/${storeId}/scan/${scanId ?? 'latest'}`,
  productFinder: (storeId: string, scanId?: string, params?: Partial<{shelf: string; search: SEARCH_STATE; term: string; gtin: string}>) => {
    if (!params) return `${routesFactory.home(storeId, scanId)}/product-finder`

    const queryParams = new URLSearchParams()
    if (params.shelf) queryParams.append('shelf', params.shelf)
    if (params.search) queryParams.append('search', params.search)
    if (params.term) queryParams.append('term', params.term)
    if (params.gtin) queryParams.append('gtin', params.gtin)
    return routesFactory.home(storeId, scanId) + '/product-finder?' + queryParams.toString()
  },
  tasks: (storeId: string, scanId?: string) => `${routesFactory.home(storeId, scanId)}/out-of-shelf-list`,
  storeMap: (storeId: string, scanId?: string, params?: Partial<{ gtin: string; shelfId: string; shelfView: ViewType; withTask: boolean; day: string}>) => {

    if (!params) return `${routesFactory.home(storeId, scanId)}/store-map`

    const queryParams = new URLSearchParams()
    if (params.gtin) queryParams.append('gtin', params.gtin)
    if (params.shelfId) queryParams.append('shelf', params.shelfId)
    if (params.shelfView) queryParams.append('shelf-view', params.shelfView)
    if (params.withTask) queryParams.append('with-tasks', 'true')
    if (params.day) queryParams.append('day', params.day)

    return `${routesFactory.home(storeId, scanId)}/store-map?${queryParams.toString()}`
  },
  panorama: (storeId: string, scanId?: string) => `${routesFactory.home(storeId, scanId)}/panorama`,
  storeMapByGtin: (storeId: string, scanId: string, gtin: string, withTasks = false) => {
    return routesFactory.storeMap(storeId, scanId, { gtin, withTask: withTasks })
  },
  storeMapByShelf: (storeId: string, scanId: string, shelfId: string) => {
    return routesFactory.storeMap(storeId, scanId, { shelfId })
  },
  storeManagement: (storeId: string, scanId?: string) => `${routesFactory.home(storeId, scanId)}/store-management`,
  account: (storeId: string, scanId?: string) => `${routesFactory.home(storeId, scanId)}/account`,
  analytics: (storeId: string, scanId?: string) => `${routesFactory.home(storeId, scanId)}/analytics`,
  analyticsShelfScanReport: (storeId: string, scanId?: string) => `${routesFactory.analytics(storeId, scanId)}/shelf-scan-report`,
  components: '/components',
  fakePrincipal: '/fake-principal',
  administration: () => '/administration',
  workflows: () => '/workflows',
  mapInvestigator: (storeId: string, scanId: string) => `${routesFactory.home(storeId, scanId)}/map-investigator`,
}

export function buildUrlWithReplacedStoreAndScan(storeId: string, scanId: string, pathname?: string) {
  const currentPathname = pathname ?? window.location.pathname

  if (currentPathname.endsWith('/product-finder')) {
    return routesFactory.productFinder(storeId, scanId)
  }

  if (currentPathname.endsWith('/out-of-shelf-list')) {
    return routesFactory.tasks(storeId, scanId)
  }

  if (currentPathname.endsWith('/store-map')) {
    return routesFactory.storeMap(storeId, scanId)
  }

  if (currentPathname.endsWith('/store-management')) {
    return routesFactory.storeManagement(storeId, scanId)
  }

  if (currentPathname.endsWith('/account')) {
    return routesFactory.account(storeId, scanId)
  }

  if (currentPathname.endsWith('/analytics/shelf-scan-report')) {
    return routesFactory.analyticsShelfScanReport(storeId, scanId)
  }

  if (currentPathname.endsWith('/analytics')) {
    return routesFactory.analytics(storeId, scanId)
  }

  if (currentPathname.endsWith('/panorama')) {
    return routesFactory.panorama(storeId, scanId)
  }

  return routesFactory.home(storeId, scanId)
}